import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';

// --- Dark theme ---

export const scadDarkTheme = EditorView.theme({
  '&': {
    backgroundColor: '#0a0b0d',
    color: '#c9ccd1',
    fontSize: '13px',
    height: '100%',
  },
  '.cm-content': {
    fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
    caretColor: '#e6b54e',
    padding: '8px 0',
    lineHeight: '1.7',
  },
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: '#e6b54e',
    borderLeftWidth: '2px',
  },
  '&.cm-focused .cm-cursor': {
    animation: 'cm-blink 1s step-end infinite',
  },
  '@keyframes cm-blink': {
    '50%': { opacity: '0' },
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: 'rgba(230,181,78,0.18)',
  },
  '.cm-activeLine': {
    backgroundColor: 'rgba(230,181,78,0.08)',
  },
  '.cm-gutters': {
    backgroundColor: '#101215',
    color: '#6b7280',
    border: 'none',
    borderRight: '1px solid #23272e',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'rgba(230,181,78,0.12)',
    color: '#e6b54e',
  },
  '.cm-foldPlaceholder': {
    backgroundColor: '#2d323a',
    color: '#9aa0a8',
    border: 'none',
  },
  '.cm-tooltip': {
    backgroundColor: '#101215',
    border: '1px solid #2d323a',
    color: '#e8e9eb',
  },
  '.cm-tooltip-autocomplete': {
    '& > ul > li[aria-selected]': {
      backgroundColor: 'rgba(230,181,78,0.18)',
      color: '#e6b54e',
    },
  },
  '.cm-tooltip-hover': {
    maxWidth: '420px',
    padding: '0',
  },
  '.scad-hover-tooltip': {
    padding: '8px 12px',
    fontSize: '12px',
    lineHeight: '1.5',
  },
  '.scad-hover-name': {
    fontWeight: 'bold',
    color: '#82b1ff',
    fontSize: '13px',
    marginBottom: '2px',
  },
  '.scad-hover-sig': {
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    fontSize: '11px',
    color: '#e6b54e',
    marginBottom: '4px',
  },
  '.scad-hover-desc': {
    color: '#c9ccd1',
    marginBottom: '6px',
  },
  '.scad-hover-params': {
    borderTop: '1px solid #2d323a',
    paddingTop: '6px',
  },
  '.scad-hover-params-title': {
    color: '#9aa0a8',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    marginBottom: '3px',
  },
  '.scad-hover-param': {
    fontSize: '11px',
    lineHeight: '1.6',
  },
  '.scad-hover-param-name': {
    color: '#c792ea',
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
  },
  '.scad-hover-param-info': {
    color: '#9aa0a8',
  },
  '.scad-signature-help': {
    padding: '4px 10px',
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    fontSize: '11px',
    color: '#c9ccd1',
  },
  '.scad-sig-active': {
    color: '#e6b54e',
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  '.scad-sig-info': {
    color: '#9aa0a8',
    fontSize: '10px',
    marginTop: '2px',
  },
  '.scad-hover-hint': {
    color: '#7d838c',
    fontSize: '10px',
    marginBottom: '0',
  },
  '.cm-line': {
    lineHeight: '1.7',
  },
}, { dark: true });

// Syntax colors come from CSS variables (`--tk-k/f/n/s/c`, `--fg-dim`, `--mute`)
// so every color profile automatically restyles the code without rebuilding
// CodeMirror. Classes are defined in index.html — see `.cm-editor .tk-*` rules.
const scadHighlightStyle = HighlightStyle.define([
  { tag: tags.keyword,                          class: 'tk-k' },
  { tag: tags.variableName,                     class: 'tk-v' },
  { tag: tags.function(tags.variableName),      class: 'tk-f' },
  { tag: tags.definition(tags.variableName),    class: 'tk-f' },
  { tag: tags.propertyName,                     class: 'tk-p' },
  { tag: tags.special(tags.variableName),       class: 'tk-n' },
  { tag: tags.number,                           class: 'tk-n' },
  { tag: tags.string,                           class: 'tk-s' },
  { tag: tags.bool,                             class: 'tk-b' },
  { tag: tags.operator,                         class: 'tk-op' },
  { tag: tags.punctuation,                      class: 'tk-pu' },
  { tag: [tags.blockComment, tags.lineComment], class: 'tk-c' },
]);

export const scadHighlighting = syntaxHighlighting(scadHighlightStyle);

// --- Light theme ---

export const scadLightTheme = EditorView.theme({
  '&': {
    backgroundColor: '#f5f3ee',
    color: '#2c2c28',
    fontSize: '13px',
    height: '100%',
  },
  '.cm-content': {
    fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
    caretColor: '#b85c2a',
    padding: '8px 0',
    lineHeight: '1.7',
  },
  '.cm-cursor, .cm-dropCursor': {
    borderLeftColor: '#b85c2a',
    borderLeftWidth: '2px',
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: 'rgba(184,92,42,0.15)',
  },
  '.cm-activeLine': {
    backgroundColor: 'rgba(184,92,42,0.06)',
  },
  '.cm-gutters': {
    backgroundColor: '#ecebe4',
    color: '#8a8578',
    border: 'none',
    borderRight: '1px solid #cfccc1',
  },
  '.cm-activeLineGutter': {
    backgroundColor: 'rgba(184,92,42,0.10)',
    color: '#b85c2a',
  },
  '.cm-foldPlaceholder': {
    backgroundColor: '#e8e8e8',
    color: '#888888',
    border: 'none',
  },
  '.cm-tooltip': {
    backgroundColor: '#ffffff',
    border: '1px solid #d0d0d8',
    color: '#333333',
  },
  '.cm-tooltip-autocomplete': {
    '& > ul > li[aria-selected]': {
      backgroundColor: '#d4e8ff',
    },
  },
  '.cm-tooltip-hover': {
    maxWidth: '420px',
    padding: '0',
  },
  '.scad-hover-tooltip': {
    padding: '8px 12px',
    fontSize: '13px',
    lineHeight: '1.5',
  },
  '.scad-hover-name': {
    fontWeight: 'bold',
    color: '#1565c0',
    fontSize: '14px',
    marginBottom: '2px',
  },
  '.scad-hover-sig': {
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    fontSize: '12px',
    color: '#0097a7',
    marginBottom: '4px',
  },
  '.scad-hover-desc': {
    color: '#333333',
    marginBottom: '6px',
  },
  '.scad-hover-params': {
    borderTop: '1px solid #e0e0e0',
    paddingTop: '6px',
  },
  '.scad-hover-params-title': {
    color: '#888888',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '3px',
  },
  '.scad-hover-param': {
    fontSize: '12px',
    lineHeight: '1.6',
  },
  '.scad-hover-param-name': {
    color: '#7b1fa2',
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
  },
  '.scad-hover-param-info': {
    color: '#888888',
  },
  '.scad-signature-help': {
    padding: '4px 10px',
    fontFamily: '"JetBrains Mono", "Fira Code", monospace',
    fontSize: '11px',
    color: '#333333',
  },
  '.scad-sig-active': {
    color: '#0097a7',
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
  '.scad-sig-info': {
    color: '#777777',
    fontSize: '10px',
    marginTop: '2px',
  },
  '.scad-hover-hint': {
    color: '#999999',
    fontSize: '10px',
    marginBottom: '0',
  },
  '.cm-line': {
    lineHeight: '1.6',
  },
}, { dark: false });

// Light mode shares the same class-based HighlightStyle — classes resolve
// to light-theme colors via CSS (`[data-theme="light"] .cm-editor .tk-*`).
export const scadLightHighlighting = syntaxHighlighting(scadHighlightStyle);
